import {
  EllipsedText,
  FlexColumn,
  LabeledXSData,
  SectionedData,
  SectionedDataT,
  useMoment,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Cell, Grid } from 'baseui/layout-grid';
import { LabelXSmall, ParagraphXSmall } from 'baseui/typography';
import { useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { ASSET_TYPE } from '../../../constants/asset';
import { useFleetHealthEvent } from '../../../hooks/useFleetHealthEvent';
import { useStyles } from '../../../hooks/useStyles';
import { AssetType } from '../../../types/asset';
import { FleetHealtEventType, FleetHealthEvent } from '../../../types/fleetHealth';
import { isVehicleLikeType } from '../../../utils/assets';
import { formatDaysMinutes } from '../../../utils/fleetHealth';

type Props = {
  event: FleetHealthEvent;
  fullData?: boolean;
};

function FleetHealthEventData({ event, fullData = false }: Readonly<Props>) {
  const { theme, css, classes } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { getEventStatusName } = useFleetHealthEvent();
  const labelWidth = '110px';
  const isExtraLarge = useWindowSize().width >= theme.breakpoints.extraLarge;
  const otherLabelWidth = isExtraLarge ? 'auto' : '110px';
  const { alarmConfig, deviceModel, lastEvent, status, eventType, asset } = event;
  const dataSize = 'small';
  const paragraphXSmallClass = css({ ...theme.typography.ParagraphXSmall });
  const otherDevice = event.otherDevice;

  const alarmConfigData = useMemo(() => {
    if (eventType === FleetHealtEventType.maxCommunication) {
      return [
        {
          label: 'Tiempo Sin CMC:',
          content: alarmConfig.deltaMinutes ? formatDaysMinutes(alarmConfig.deltaMinutes) : '-',
        },
      ];
    }
    if (eventType === FleetHealtEventType.batteryLevel) {
      return [
        {
          label: 'Batería actual:',
          content: alarmConfig.currentBattery ? alarmConfig.currentBattery.toString() + '%' : '-',
        },
      ];
    }
    return [];
  }, [alarmConfig, eventType]);

  const generalData = useMemo(() => {
    const data = [
      {
        label: 'Modelo:',
        content: deviceModel.name || '-',
      },
      {
        label: 'Ident:',
        content: asset.device.ident || '-',
      },
      {
        label: 'Etiqueta:',
        content: asset.label || '-',
      },
    ];
    if (fullData) {
      data.push({
        label: 'Tipo:',
        content: (ASSET_TYPE as { [key: string]: string })[asset.assetType as string] ?? '-',
      });
    }
    if (!fullData) {
      data.push(
        ...[
          {
            label: 'Cliente:',
            content: asset.client.name || '-',
          },
          {
            label: 'Subcliente:',
            content: asset.subClient.name || '-',
          },
        ]
      );
    }
    data.push({
      label: asset.vehicle?.vin ? 'Vin:' : 'IdAsset:',
      content: asset.vehicle?.vin ?? (asset._id || '-'),
    });
    if (!fullData) {
      return [
        {
          label: 'Estado:',
          content: getEventStatusName(status),
        },
        ...data,
        {
          label: <LabelXSmall maxWidth={labelWidth}>Vencimiento de la Poliza:</LabelXSmall>,
          content: asset.insuranceEndDate ? formatDateFromTs(asset.insuranceEndDate, 'yyyy-MM-DD HH:mm:ss') : '-',
        },
      ];
    }
    if (fullData && isVehicleLikeType(asset.assetType as AssetType)) {
      data.push(
        ...[
          {
            label: 'Marca:',
            content: asset.vehicle?.brand?.name ?? '-',
          },
          {
            label: 'Submarca:',
            content: asset.vehicle?.subBrand?.name ?? '-',
          },
          {
            label: 'Placa:',
            content: asset.vehicle?.licensePlate ?? '-',
          },
        ]
      );
    }
    return [
      ...data,
      {
        label: <LabelXSmall maxWidth={labelWidth}>Vencimiento de la Poliza:</LabelXSmall>,
        content: asset.insuranceEndDate ? formatDateFromTs(asset.insuranceEndDate, 'yyyy-MM-DD HH:mm:ss') : '-',
      },
    ];
  }, [deviceModel, fullData, status, asset]);

  const lastEventData = useMemo(() => {
    const data: SectionedDataT[] = [
      {
        label: 'Fecha y hora:',
        content: formatDateFromTs(lastEvent.timestamp, 'yyyy-MM-DD HH:mm:ss') || '-',
      },
      {
        label: 'Tipo de reporte:',
        content: lastEvent.notificationType || '-',
      },
      {
        label: 'Lat / Long:',
        content:
          lastEvent.latitude && lastEvent.longitude ? `${lastEvent.latitude}, ${lastEvent.longitude}`.toString() : '-',
      },
      {
        label: 'Velocidad:',
        content: lastEvent.speed + ' km/h',
      },
    ];
    if (fullData) {
      data.push(
        ...[
          {
            label: 'Batería:',
            content: lastEvent.batteryPercentage + '%' || '-',
          },
          {
            label: 'Georeferencia:',
            content: lastEvent.geoReference ? (
              <ParagraphXSmall margin={0}>
                <EllipsedText maxLines={5}>{lastEvent.geoReference}</EllipsedText>
              </ParagraphXSmall>
            ) : (
              '-'
            ),
          },
        ]
      );
    }
    return data;
  }, [fullData, lastEvent]);

  const clientData = useMemo(() => {
    const data: SectionedDataT[] = [
      {
        label: 'Nombre:',
        content: asset.client.name,
      },
    ];
    if (asset.client.contact?.phone) {
      data.push(
        ...[
          {
            label: 'Contacto:',
            content: asset.client.contact.fullName ?? '-',
          },
          {
            label: '',
            content: (
              <a
                href={`tel:${asset.client.contact.phone}`}
                className={paragraphXSmallClass}
              >
                {asset.client.contact.phone}
              </a>
            ),
          },
        ]
      );
    }
    if (asset.client.invoiceContact?.phone) {
      data.push(
        ...[
          {
            label: 'Cto. facturación:',
            content: asset.client.invoiceContact.fullName ?? '-',
          },
          {
            label: '',
            content: (
              <a
                href={`tel:${asset.client.invoiceContact.phone}`}
                className={paragraphXSmallClass}
              >
                {asset.client.invoiceContact.phone}
              </a>
            ),
          },
        ]
      );
    }
    if (asset.client.installationContact?.phone) {
      data.push(
        ...[
          {
            label: 'Cto. instalación:',
            content: asset.client.installationContact.fullName ?? '-',
          },
          {
            label: '',
            content: (
              <a
                href={`tel:${asset.client.installationContact.phone}`}
                className={paragraphXSmallClass}
              >
                {asset.client.installationContact.phone}
              </a>
            ),
          },
        ]
      );
    }
    return data;
  }, [fullData, asset]);

  const subClientData = useMemo(() => {
    const data: SectionedDataT[] = [
      {
        label: 'Nombre:',
        content: asset.subClient.name,
      },
    ];
    if (asset.subClient.contact?.phone) {
      data.push(
        ...[
          {
            label: 'Contacto:',
            content: asset.subClient.contact.fullName ?? '-',
          },
          {
            label: '',
            content: (
              <a
                href={`tel:${asset.subClient.contact.phone}`}
                className={paragraphXSmallClass}
              >
                {asset.subClient.contact.phone}
              </a>
            ),
          },
        ]
      );
    }
    return data;
  }, [fullData, asset]);

  return (
    <Grid
      gridColumns={12}
      gridGaps={[4, 8]}
      gridGutters={[12, 16]}
      gridMargins={[0]}
      behavior="fluid"
      overrides={{
        GridWrapper: {
          style: {
            margin: 0,
          },
        },
      }}
    >
      <Cell span={[12, 12, 12, 6, 6]}>
        <FlexColumn>
          <SectionedData
            title="Información general"
            labelWidth={labelWidth}
            data={generalData}
            size={dataSize}
            containerGap={0}
            dataGap={0}
          />
        </FlexColumn>
      </Cell>
      <Cell span={[12, 12, 12, 6, 6]}>
        <FlexColumn gap={theme.sizing.scale200}>
          {eventType !== FleetHealtEventType.alarms && (
            <SectionedData
              title="Motivo de alarma"
              labelWidth={labelWidth}
              data={alarmConfigData}
              size={dataSize}
              containerGap={0}
              dataGap={0}
            />
          )}
          <SectionedData
            title="Último evento"
            labelWidth={labelWidth}
            data={lastEventData}
            size={dataSize}
            containerGap={0}
            dataGap={0}
          />
        </FlexColumn>
      </Cell>
      {fullData && (
        <>
          <Cell span={[12, 12, 12, 6, 6]}>
            <SectionedData
              title="Cliente"
              labelWidth={labelWidth}
              data={clientData}
              size={dataSize}
              containerGap={0}
              dataGap={0}
            />
          </Cell>
          <Cell span={[12, 12, 12, 6, 6]}>
            <SectionedData
              title="Subcliente"
              labelWidth={labelWidth}
              data={subClientData}
              size={dataSize}
              containerGap={0}
              dataGap={0}
            />
          </Cell>
          {otherDevice && (
            <Cell span={[12]}>
              <FlexColumn gap={0}>
                <LabelXSmall className={classes.dataHeaderClass}>Equipo complementario</LabelXSmall>
                <Block
                  className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    [theme.mediaQuery.extraLarge]: {
                      flexDirection: 'row',
                      columnGap: theme.sizing.scale600,
                      justifyContent: 'space-between',
                    },
                  })}
                >
                  <LabeledXSData
                    label="Fecha y hora:"
                    labelWidth={otherLabelWidth}
                    content={
                      <ParagraphXSmall margin={0}>
                        <EllipsedText inline={false}>
                          {formatDateFromTs(otherDevice.timestamp, 'yyyy-MM-DD HH:mm:ss') || '-'}
                        </EllipsedText>
                      </ParagraphXSmall>
                    }
                  />
                  <LabeledXSData
                    label="Tipo de reporte:"
                    labelWidth={otherLabelWidth}
                    content={
                      <ParagraphXSmall margin={0}>
                        <EllipsedText inline={false}>{otherDevice.notificationType || '-'}</EllipsedText>
                      </ParagraphXSmall>
                    }
                  />
                  <LabeledXSData
                    label="Lat / Long:"
                    labelWidth={otherLabelWidth}
                    content={
                      <ParagraphXSmall margin={0}>
                        <EllipsedText inline={false}>
                          {otherDevice.latitude && otherDevice.longitude
                            ? `${otherDevice.latitude}, ${otherDevice.longitude}`.toString()
                            : '-'}
                        </EllipsedText>
                      </ParagraphXSmall>
                    }
                  />
                </Block>
              </FlexColumn>
            </Cell>
          )}
        </>
      )}
    </Grid>
  );
}

export default FleetHealthEventData;
